import { useState } from 'react';
import {
  AppFormDialog,
  AppMenuSheet,
  AppScreenScaffold,
  AppSectionCard,
  AppSectionDivider,
  AppSectionRow,
  AppSwitch,
  AppTextField,
  AppTopBar,
  AppUndoSnackbar,
} from '@core/designsystem';
import type { ThemeMode } from '@core/designsystem';
import { ARCHIVE_HINT, STATS_TEXT, STORAGE_WARNING } from './formatter';
import type { ArchiveActions, ArchiveState } from './useArchive';
import type { SettingsActions, SettingsState } from './useSettings';
import './settings.css';

const THEME_LABEL: Record<ThemeMode, string> = {
  system: 'Как в системе',
  light: 'Светлая',
  dark: 'Тёмная',
};

const THEMES: ThemeMode[] = ['system', 'light', 'dark'];

/** Куда писать разработчику. Тот же адрес, что в Android-версии. */
const DEVELOPER_EMAIL = 'support@docsbox.ru';

/**
 * Настройки — «Структура» §14, хранилище — «Паспорт» §9.
 *
 * Предупреждение о хранении стоит **выше** всего остального: в вебе иконку сносят
 * одним долгим нажатием, и данные уходят молча ([D-47](docs/DECISIONS.md)). Это
 * единственное, что юрист обязан прочитать на этом экране.
 */
export function SettingsScreen({
  state,
  actions,
  archive,
  archiveActions,
  onBack,
  onOpenCase,
}: {
  state: SettingsState;
  actions: SettingsActions;
  archive: ArchiveState;
  archiveActions: ArchiveActions;
  onBack: () => void;
  onOpenCase: (caseId: string) => void;
}) {
  const [editingName, setEditingName] = useState(false);
  const [pickingTheme, setPickingTheme] = useState(false);

  return (
    <AppScreenScaffold
      topBar={<AppTopBar title="Настройки" onBack={onBack} />}
      overlay={
        <>
          {editingName ? (
            <NameDialog
              value={state.name}
              onDismiss={() => setEditingName(false)}
              onConfirm={(value) => {
                actions.setName(value);
                setEditingName(false);
              }}
            />
          ) : null}

          {pickingTheme ? (
            <AppMenuSheet
              title="Тема"
              onDismiss={() => setPickingTheme(false)}
              items={THEMES.map((mode) => ({
                icon: mode === state.theme ? ('check' as const) : ('settings' as const),
                label: THEME_LABEL[mode],
                onSelect: () => actions.setTheme(mode),
              }))}
            />
          ) : null}

          {archive.pending ? (
            <AppFormDialog
              title="Загрузить архив"
              onDismiss={archiveActions.cancel}
              confirm={{ label: 'Загрузить', onClick: () => void archiveActions.confirm() }}
            >
              {/*
                Что внутри — до нажатия, а не после. Архив ничего не стирает: дела
                сливаются по совпадению, и то, что уже есть на устройстве, остаётся.
              */}
              <p className="settings__warning-text">{archive.pending}</p>
            </AppFormDialog>
          ) : null}

          {state.message || archive.message || archive.error ? (
            <AppUndoSnackbar
              text={state.message ?? archive.message ?? archive.error ?? ''}
              actionLabel="Понятно"
              onUndo={() => {
                actions.messageShown();
                archiveActions.shown();
              }}
              onExpire={() => {
                actions.messageShown();
                archiveActions.shown();
              }}
            />
          ) : null}
        </>
      }
    >
      <AppSectionCard title="Хранение данных">
        <div className="settings__warning">
          <p className="settings__warning-text">{STORAGE_WARNING}</p>
        </div>
      </AppSectionCard>

      {/*
        Архив стоит сразу под предупреждением: это ответ на него. Сказать, что данные
        пропадут вместе с иконкой, и не сказать, как их сохранить, — половина правды.
      */}
      <AppSectionCard title="Архив">
        <div className="settings__warning">
          <p className="settings__hint-text">{ARCHIVE_HINT}</p>
        </div>
        <AppSectionDivider />

        {archive.ready ? (
          <AppSectionRow
            icon="share"
            title="Поделиться архивом"
            subtitle={archive.ready}
            action
            onClick={archiveActions.send}
          />
        ) : (
          <AppSectionRow
            icon="folder"
            title={archive.busy === 'building' ? 'Собираем архив…' : 'Подготовить архив'}
            action
            onClick={() => void archiveActions.prepare()}
          />
        )}

        {archive.incomplete ? (
          <>
            <AppSectionDivider />
            <AppSectionRow icon="error" title={archive.incomplete} />
          </>
        ) : null}

        <AppSectionDivider />
        <AppSectionRow
          icon="upload_file"
          title={archive.busy === 'reading' ? 'Читаем архив…' : 'Загрузить архив'}
          subtitle={archive.busy === 'applying' ? 'Переносим дела…' : undefined}
          picker={{ accept: '.zip,application/zip', onFiles: (files) => void archiveActions.pick(files) }}
        />
      </AppSectionCard>

      <AppSectionCard title="Хранилище">
        <AppSectionRow icon="folder" title={state.storageLine} subtitle={`Данные в ${state.storageKind}`} />
        {state.heavyCases.length > 0 ? <AppSectionDivider /> : null}
        {state.heavyCases.map((item, index) => (
          <div key={item.id}>
            {index > 0 ? <AppSectionDivider /> : null}
            <AppSectionRow
              icon="picture_as_pdf"
              title={item.title}
              subtitle={item.meta}
              onClick={() => onOpenCase(item.id)}
            />
          </div>
        ))}
        <AppSectionDivider />
        {state.reclaimable ? (
          <AppSectionRow
            icon="delete"
            title={state.isCleaning ? 'Убираем…' : state.reclaimable}
            action
            onClick={() => void actions.cleanUp()}
          />
        ) : (
          // Ноль означает, что убирать нечего, — и об этом лучше сказать, чем показать
          // кнопку, которая ничего не делает.
          <AppSectionRow icon="check" title="Лишних файлов нет" />
        )}
      </AppSectionCard>

      <AppSectionCard title="Профиль">
        <AppSectionRow
          icon={state.name ? 'person' : 'add'}
          title={state.name || 'Добавить имя'}
          subtitle={state.name ? 'Имя' : undefined}
          action={!state.name}
          onClick={() => setEditingName(true)}
        />
        <AppSectionDivider />
        <AppSectionRow
          icon="settings"
          title={THEME_LABEL[state.theme]}
          subtitle="Тема"
          onClick={() => setPickingTheme(true)}
        />
      </AppSectionCard>

      {/*
        Статистика — [D-23](docs/DECISIONS.md).

        Счётчики копятся локально всегда, а уходят, пока включён этот выключатель;
        включён он по умолчанию ([D-30](docs/DECISIONS.md)). Рядом — сами цифры
        за сегодня: обещание «мы не видим ваших документов» стоит ровно столько,
        сколько стоит возможность это проверить.
      */}
      <AppSectionCard title="Статистика">
        <AppSectionRow
          icon="checklist"
          title="Отправлять обезличенные цифры"
          subtitle={STATS_TEXT}
          trailing={
            <AppSwitch
              checked={state.isStatsSendingEnabled}
              label="Отправлять обезличенные цифры"
              onChange={actions.setStatsSending}
            />
          }
        />

        {state.todayStats.length > 0 ? (
          <>
            <AppSectionDivider />
            <AppSectionRow
              icon="event"
              title={`Сегодня посчитано: ${state.todayStats.join(', ')}`}
            />
          </>
        ) : null}

        <AppSectionDivider />
        {/*
          «Отправить сейчас» — не для юриста, а для проверки: без неё нельзя убедиться
          на живом телефоне, что цифры доходят. Сама по себе задача идёт при запуске
          и при возвращении к приложению, не чаще раза в час.
        */}
        <AppSectionRow
          icon="share"
          title={state.isSendingStats ? 'Отправляем…' : 'Отправить сейчас'}
          subtitle="Обычно цифры уходят сами при запуске приложения"
          action
          onClick={() => void actions.sendStatsNow()}
        />

        <AppSectionDivider />
        {/*
          Сброс номера установки. Цифры, присланные раньше, после него не связываются
          с новыми — это и есть смысл кнопки, а не побочный эффект.
        */}
        <AppSectionRow
          icon="settings"
          title="Сбросить номер установки"
          subtitle="Прежние присланные цифры перестанут связываться с этим устройством"
          action
          onClick={actions.resetInstallId}
        />

        {!state.isStatsConfigured ? (
          <>
            <AppSectionDivider />
            {/*
              Сборка без настроенного приёмника ничего никуда не шлёт. Говорим об этом
              прямо: выключатель, который ничего не выключает, — обман.
            */}
            <AppSectionRow icon="error" title="В этой сборке отправка не настроена — цифры остаются на устройстве" />
          </>
        ) : null}
      </AppSectionCard>

      <AppSectionCard title="О приложении">
        <AppSectionRow icon="event" title={state.version} subtitle="Версия" />
        <AppSectionDivider />
        <a className="settings__link" href={`mailto:${DEVELOPER_EMAIL}?subject=Yatwa ${state.version}`}>
          <AppSectionRow icon="mail" title="Написать разработчику" subtitle={DEVELOPER_EMAIL} />
        </a>
      </AppSectionCard>
    </AppScreenScaffold>
  );
}

function NameDialog({
  value,
  onDismiss,
  onConfirm,
}: {
  value: string;
  onDismiss: () => void;
  onConfirm: (value: string) => void;
}) {
  const [name, setName] = useState(value);

  return (
    <AppFormDialog
      title="Имя"
      onDismiss={onDismiss}
      confirm={{ label: 'Готово', onClick: () => onConfirm(name.trim()) }}
    >
      <AppTextField value={name} onChange={setName} label="Как к вам обращаться" autoFocus />
    </AppFormDialog>
  );
}
